import React from "react";
import {
  Card,
  Alert,
  Collapse,
  Tag,
  Space,
  Typography,
  Descriptions,
  List,
  Empty,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import {
  ValidationResponse,
  IssueSeverity,
  NetworkValidationResult,
  AdapterValidationResult,
  ValidationIssue,
} from "../../types/validator";
import { ValidationStats } from "./ValidationStats";

const { Text } = Typography;

const getSeverityIcon = (severity: IssueSeverity) => {
  switch (severity) {
    case "error":
      return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
    case "warning":
      return <ExclamationCircleOutlined style={{ color: "#faad14" }} />;
    case "info":
      return <InfoCircleOutlined style={{ color: "#1890ff" }} />;
    default:
      return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
  }
};

const getStatusAlertType = (
  severity: IssueSeverity
): "success" | "warning" | "error" | "info" => {
  switch (severity) {
    case "error":
      return "error";
    case "warning":
      return "warning";
    case "info":
      return "info";
    default:
      return "success";
  }
};

const IssuesList: React.FC<{ issues: ValidationIssue[] }> = ({ issues }) => {
  if (issues.length === 0) return null;

  return (
    <List
      size="small"
      dataSource={issues}
      renderItem={(issue) => (
        <List.Item>
          <Space>
            {getSeverityIcon(issue.severity)}
            <Text>{issue.message}</Text>
            {issue.details && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({issue.details})
              </Text>
            )}
          </Space>
        </List.Item>
      )}
    />
  );
};

const AdapterDetails: React.FC<{ adapter: AdapterValidationResult }> = ({
  adapter,
}) => {
  return (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Text strong>Version: {adapter.adapterVersion}</Text>
        {adapter.adapterId && (
          <Link to={`/wizard/adapter/${adapter.adapterId}`}>
            <LinkOutlined /> View Adapter
          </Link>
        )}
      </Space>
      {adapter.childSdksStatus.length > 0 && (
        <Descriptions
          size="small"
          column={1}
          style={{ marginTop: 8 }}
          bordered
        >
          {adapter.childSdksStatus.map((sdk, idx) => (
            <Descriptions.Item
              key={idx}
              label={
                <Space>
                  {sdk.found ? (
                    sdk.satisfiesConstraint ? (
                      <CheckCircleOutlined style={{ color: "#52c41a" }} />
                    ) : (
                      <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                    )
                  ) : (
                    <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                  )}
                  <Text>{sdk.sdkName}</Text>
                </Space>
              }
            >
              <Space>
                <Tag>
                  {sdk.constraintOperator} {sdk.constraintVersion}
                </Tag>
                {sdk.found && sdk.actualVersion && (
                  <Tag color={sdk.satisfiesConstraint ? "green" : "orange"}>
                    Actual: {sdk.actualVersion}
                  </Tag>
                )}
                {!sdk.found && <Tag color="red">Not Found</Tag>}
              </Space>
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}
      <IssuesList issues={adapter.issues} />
    </div>
  );
};

const NetworkCard: React.FC<{ network: NetworkValidationResult }> = ({
  network,
}) => {
  const hasIssues =
    network.issues.length > 0 ||
    network.adapters.some((a) => a.issues.length > 0);

  const getCardHeaderExtra = () => {
    if (!network.adapterFound) {
      return <Tag color="red">Missing</Tag>;
    }
    if (hasIssues) {
      return <Tag color="orange">Issues</Tag>;
    }
    return <Tag color="green">OK</Tag>;
  };

  return (
    <Collapse
      style={{ marginBottom: 8 }}
      items={[
        {
          key: network.network,
          label: (
            <Space>
              <Text strong>{network.displayName}</Text>
              {network.expectedArtifact && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  ({network.expectedArtifact})
                </Text>
              )}
            </Space>
          ),
          extra: getCardHeaderExtra(),
          children: (
            <>
              {!network.adapterFound ? (
                <Alert
                  type="warning"
                  message="Adapter not found in this release"
                  showIcon
                />
              ) : (
                <>
                  {network.adapterVersions.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary">Available versions: </Text>
                      {network.adapterVersions.map((v) => (
                        <Tag key={v}>{v}</Tag>
                      ))}
                    </div>
                  )}
                  {network.adapters.map((adapter, idx) => (
                    <AdapterDetails key={idx} adapter={adapter} />
                  ))}
                </>
              )}
              <IssuesList issues={network.issues} />
            </>
          ),
        },
      ]}
    />
  );
};

/**
 * Presentational rendering of a mediation ValidationResponse: summary
 * statistics + per-network/adapter issue breakdown. Used by ValidatorDrawer
 * (embedded in SdkViewPage) to render the full validation breakdown.
 *
 * `showStats` defaults to true; ValidatorDrawer passes `false` since
 * SdkViewPage already renders the same <ValidationStats/> row inline above
 * the drawer, so repeating it there would be redundant.
 */
export const ValidationResult: React.FC<{
  result: ValidationResponse;
  showStats?: boolean;
}> = ({ result, showStats = true }) => {
  const { summary, overallStatus } = result;

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <Alert
          type={getStatusAlertType(overallStatus)}
          message={`Validation ${overallStatus === "error" ? "Failed" : overallStatus === "warning" ? "Passed with Warnings" : "Passed"}`}
          description={`${result.mediation} ${result.platform} v${result.version}`}
          showIcon
          style={showStats ? { marginBottom: 16 } : undefined}
        />
        {showStats && <ValidationStats summary={summary} />}
      </Card>

      {result.networks.length === 0 ? (
        <Empty description="No networks found in this release" />
      ) : (
        <Card title="Network Details">
          {result.networks.map((network) => (
            <NetworkCard key={network.network} network={network} />
          ))}
        </Card>
      )}
    </>
  );
};
